import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText, Plus, Loader2, Send, Copy, Edit, Trash2, ArrowUp, ArrowDown,
  X, Eye, Link as LinkIcon, ExternalLink, ClipboardList, GripVertical,
  CheckCircle2, Clock, XCircle, User, Sparkles, Gavel, FileCheck2,
} from "lucide-react";

const inputCls = "h-11 bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

const TIPOS_CAMPO = [
  { value: "text", label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Teléfono" },
  { value: "cedula", label: "Cédula (CR)" },
  { value: "select", label: "Selección única" },
  { value: "multiselect", label: "Selección múltiple" },
  { value: "boolean", label: "Sí / No" },
];

const TIPOS_ESCRITURA = [
  "Compraventa", "Poder General", "Poder Especial", "Testamento",
  "Constitución de Sociedad", "Hipoteca", "Donación", "Fideicomiso", "Otro",
];

const ESTADOS_ENVIO = {
  pendiente: { label: "Pendiente", cls: "bg-[#FBEED4] text-[#7A5312] border-[#E5C892]", Icon: Clock },
  completado: { label: "Completado", cls: "bg-[#D8EFDD] text-[#155724] border-[#7BC48A]", Icon: CheckCircle2 },
  expirado: { label: "Expirado", cls: "bg-[#FBEDEF] text-[#7A1E2E] border-[#E5A5B0]", Icon: XCircle },
};

const emptyCampo = () => ({ id: "", tipo: "text", label: "", placeholder: "", help_text: "", required: false, options: [] });
const emptySeccion = () => ({ id: "", titulo: "Nueva sección", descripcion: "", campos: [emptyCampo()] });

// Convierte un formulario del backend en secciones normalizadas (retro-compat)
const toSecciones = (f) => {
  if (!f) return [emptySeccion()];
  if (Array.isArray(f.secciones) && f.secciones.length) return f.secciones;
  if (Array.isArray(f.campos) && f.campos.length) return [{ id: "s_default", titulo: "General", descripcion: "", campos: f.campos }];
  return [emptySeccion()];
};

// ============================================================================
export default function Formularios() {
  const [tab, setTab] = useState("plantillas");
  const [formularios, setFormularios] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builder, setBuilder] = useState(null);        // {mode, formulario}
  const [envDialog, setEnvDialog] = useState(null);    // formulario para enviar
  const [envioDetail, setEnvioDetail] = useState(null);
  const [delTarget, setDelTarget] = useState(null);    // {kind, item}

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/formularios"),
      api.get("/formularios/envios"),
      api.get("/clientes"),
    ])
      .then(([f, e, c]) => { setFormularios(f.data); setEnvios(e.data); setClientes(c.data); })
      .catch(() => toast.error("Error cargando formularios"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async () => {
    if (!delTarget) return;
    const { kind, item } = delTarget;
    try {
      if (kind === "form") await api.delete(`/formularios/${item.id}`);
      else await api.delete(`/formularios/envios/${item.id}`);
      toast.success("Eliminado");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="module-formularios">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
            <FileText className="h-6 w-6 text-[#7A1E2E]" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#1A0810]">Formularios</h1>
            <p className="mt-1 text-sm text-[#4a3838] max-w-2xl">
              Diseñá formularios reutilizables, enviálos a tus clientes por enlace y auto-cargá los datos en una escritura notarial.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setBuilder({ mode: "create", formulario: { titulo: "", descripcion: "", tipo_escritura: "", secciones: [emptySeccion()] } })}
          data-testid="btn-crear-formulario"
          className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4] h-11 px-5"
        >
          <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Crear formulario
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5EEE0] p-1">
          <TabsTrigger value="plantillas" data-testid="tab-plantillas">
            Plantillas <span className="ml-2 text-xs text-[#6B5D57]">({formularios.length})</span>
          </TabsTrigger>
          <TabsTrigger value="envios" data-testid="tab-envios">
            Envíos <span className="ml-2 text-xs text-[#6B5D57]">({envios.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plantillas" className="mt-6">
          {loading ? (
            <div className="p-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E] mx-auto" /></div>
          ) : formularios.length === 0 ? (
            <EmptyState onCreate={() => setBuilder({ mode: "create", formulario: { titulo: "", descripcion: "", tipo_escritura: "", secciones: [emptySeccion()] } })} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {formularios.map((f) => (
                <FormCard key={f.id} formulario={f}
                  onEnviar={() => setEnvDialog(f)}
                  onEditar={() => setBuilder({ mode: "edit", formulario: f })}
                  onEliminar={() => setDelTarget({ kind: "form", item: f })}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="envios" className="mt-6">
          <EnviosTable
            envios={envios}
            loading={loading}
            onDetalle={(env) => setEnvioDetail(env)}
            onEliminar={(env) => setDelTarget({ kind: "envio", item: env })}
          />
        </TabsContent>
      </Tabs>

      {builder && (
        <FormularioBuilder
          state={builder}
          onClose={() => setBuilder(null)}
          onSaved={() => { setBuilder(null); load(); }}
        />
      )}
      {envDialog && (
        <EnviarDialog
          formulario={envDialog}
          clientes={clientes}
          onClose={() => setEnvDialog(null)}
          onSent={() => { setEnvDialog(null); load(); setTab("envios"); }}
        />
      )}
      {envioDetail && (
        <EnvioDetalleDialog
          envio={envioDetail}
          clientes={clientes}
          onClose={() => setEnvioDetail(null)}
          onChange={load}
        />
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">
              Eliminar {delTarget?.kind === "form" ? "formulario" : "envío"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              {delTarget?.kind === "form"
                ? "También se eliminarán todos los envíos asociados a esta plantilla. Esta acción no se puede deshacer."
                : "Se eliminará este envío y sus respuestas. Esta acción no se puede deshacer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-form"
              className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
function EmptyState({ onCreate }) {
  return (
    <div className="rounded-xl bg-white border-2 border-dashed border-[#E5DFD3] p-16 text-center">
      <div className="h-14 w-14 rounded-full bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center mx-auto">
        <ClipboardList className="h-6 w-6 text-[#7A1E2E]" strokeWidth={1.5} />
      </div>
      <h2 className="mt-5 font-display text-2xl text-[#1A0810]">Aún no tenés plantillas</h2>
      <p className="mt-3 text-sm text-[#6B5D57] max-w-lg mx-auto leading-relaxed">
        Creá tu primera plantilla para recolectar información de clientes (datos personales, propiedad, testamento, etc.) y agilizar la redacción de escrituras.
      </p>
      <Button onClick={onCreate} data-testid="empty-crear-formulario" className="mt-6 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
        <Plus className="h-4 w-4 mr-2" strokeWidth={2} /> Crear primera plantilla
      </Button>
    </div>
  );
}

function FormCard({ formulario, onEnviar, onEditar, onEliminar }) {
  return (
    <div className="rounded-xl bg-white border border-[#E5DFD3] p-5 flex flex-col" data-testid={`form-card-${formulario.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-[#1A0810] truncate">{formulario.titulo}</div>
          <div className="text-xs text-[#6B5D57] mt-0.5">{(formulario.campos || []).length} campos</div>
        </div>
        <Badge className="bg-[#F5EEE0] text-[#7A1E2E] border border-[#E5DFD3] font-semibold flex-shrink-0">
          {formulario.tipo_escritura || "General"}
        </Badge>
      </div>
      {formulario.descripcion && (
        <p className="mt-3 text-sm text-[#4a3838] line-clamp-3">{formulario.descripcion}</p>
      )}
      <div className="mt-auto pt-5 flex items-center gap-1.5">
        <Button size="sm" onClick={onEnviar} data-testid={`form-enviar-${formulario.id}`}
          className="flex-1 bg-[#1F5B2E] hover:bg-[#144320] text-white">
          <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} /> Enviar
        </Button>
        <Button size="sm" variant="outline" onClick={onEditar} data-testid={`form-editar-${formulario.id}`}
          className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">
          <Edit className="h-3.5 w-3.5" strokeWidth={1.8} />
        </Button>
        <Button size="sm" variant="ghost" onClick={onEliminar} data-testid={`form-eliminar-${formulario.id}`}
          className="text-[#7A1E2E] hover:bg-[#FBEDEF]">
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
function FormularioBuilder({ state, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...state.formulario,
    secciones: toSecciones(state.formulario),
  }));
  const [saving, setSaving] = useState(false);

  const updSec = (sIdx, patch) => setForm((s) => ({
    ...s,
    secciones: s.secciones.map((se, i) => (i === sIdx ? { ...se, ...patch } : se)),
  }));

  const addSeccion = () => setForm((s) => ({ ...s, secciones: [...s.secciones, emptySeccion()] }));

  const removeSeccion = (sIdx) => setForm((s) => ({
    ...s,
    secciones: s.secciones.length > 1 ? s.secciones.filter((_, i) => i !== sIdx) : s.secciones,
  }));

  const moveSeccion = (sIdx, dir) => setForm((s) => {
    const j = sIdx + dir;
    if (j < 0 || j >= s.secciones.length) return s;
    const arr = [...s.secciones];
    [arr[sIdx], arr[j]] = [arr[j], arr[sIdx]];
    return { ...s, secciones: arr };
  });

  const addCampo = (sIdx) => updSec(sIdx, { campos: [...form.secciones[sIdx].campos, emptyCampo()] });

  const updateCampo = (sIdx, cIdx, patch) => updSec(sIdx, {
    campos: form.secciones[sIdx].campos.map((c, i) => (i === cIdx ? { ...c, ...patch } : c)),
  });

  const removeCampo = (sIdx, cIdx) => updSec(sIdx, {
    campos: form.secciones[sIdx].campos.filter((_, i) => i !== cIdx),
  });

  const moveCampo = (sIdx, cIdx, dir) => {
    const arr = [...form.secciones[sIdx].campos];
    const j = cIdx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[cIdx], arr[j]] = [arr[j], arr[cIdx]];
    updSec(sIdx, { campos: arr });
  };

  const totalCampos = (form.secciones || []).reduce((n, s) => n + (s.campos?.length || 0), 0);

  const save = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    if (totalCampos === 0) { toast.error("Agregá al menos un campo"); return; }
    for (const s of form.secciones) {
      for (const c of s.campos || []) {
        if (!c.label?.trim()) { toast.error("Todos los campos deben tener nombre"); return; }
        if (["select", "multiselect"].includes(c.tipo) && (!c.options || c.options.length === 0)) {
          toast.error(`El campo '${c.label}' necesita opciones`); return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo,
        descripcion: form.descripcion,
        tipo_escritura: form.tipo_escritura,
        secciones: form.secciones,
      };
      if (state.mode === "create") await api.post("/formularios", payload);
      else await api.put(`/formularios/${state.formulario.id}`, payload);
      toast.success(state.mode === "create" ? "Formulario creado" : "Formulario actualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="form-builder-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810]">
            {state.mode === "create" ? "Crear formulario" : "Editar formulario"}
          </DialogTitle>
          <DialogDescription className="text-[#6B5D57]">
            Diseñá el formulario en secciones (ej. "Compareciente 1", "Compareciente 2", "Datos de la propiedad").
            Cada respuesta quedará agrupada por sección al crear la escritura.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-lg bg-white border border-[#E5DFD3] p-4 space-y-4">
            <div>
              <Label className={labelCls}>Título *</Label>
              <Input
                data-testid="form-titulo"
                value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ej: Datos para escritura de compraventa"
                className={`${inputCls} mt-2`}
              />
            </div>
            <div>
              <Label className={labelCls}>Descripción</Label>
              <Textarea
                data-testid="form-descripcion"
                value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                placeholder="Instrucciones que verá el cliente antes de completar el formulario"
                className="mt-2 min-h-[70px] bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
              />
            </div>
            <div>
              <Label className={labelCls}>Tipo de escritura a generar</Label>
              <Select value={form.tipo_escritura || ""} onValueChange={(v) => setForm({ ...form, tipo_escritura: v === "general" ? "" : v })}>
                <SelectTrigger data-testid="form-tipo-escritura" className={`${inputCls} mt-2`}>
                  <SelectValue placeholder="General (sin tipo específico)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  {TIPOS_ESCRITURA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-lg text-[#1A0810]">Secciones del formulario</div>
              <div className="text-xs text-[#6B5D57]">
                {form.secciones.length} {form.secciones.length === 1 ? "sección" : "secciones"} · {totalCampos} campos en total
              </div>
            </div>
            <Button onClick={addSeccion} data-testid="form-add-seccion" size="sm"
              className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Nueva sección
            </Button>
          </div>

          <div className="space-y-4">
            {form.secciones.map((sec, sIdx) => (
              <SeccionEditor
                key={sIdx} sIdx={sIdx} sec={sec} canDelete={form.secciones.length > 1}
                canMoveUp={sIdx > 0} canMoveDown={sIdx < form.secciones.length - 1}
                onPatch={(patch) => updSec(sIdx, patch)}
                onDelete={() => removeSeccion(sIdx)}
                onMove={(dir) => moveSeccion(sIdx, dir)}
                onAddCampo={() => addCampo(sIdx)}
                onUpdCampo={(cIdx, patch) => updateCampo(sIdx, cIdx, patch)}
                onRemoveCampo={(cIdx) => removeCampo(sIdx, cIdx)}
                onMoveCampo={(cIdx, dir) => moveCampo(sIdx, cIdx, dir)}
              />
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="form-save"
            className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar formulario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SeccionEditor({ sIdx, sec, canDelete, canMoveUp, canMoveDown, onPatch, onDelete, onMove, onAddCampo, onUpdCampo, onRemoveCampo, onMoveCampo }) {
  return (
    <div className="rounded-xl bg-white border-2 border-[#E5DFD3] overflow-hidden" data-testid={`seccion-editor-${sIdx}`}>
      <div className="bg-[#F5EEE0] px-4 py-3 flex items-start gap-3 border-b border-[#E5DFD3]">
        <div className="h-8 w-8 rounded-md bg-[#7A1E2E] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {sIdx + 1}
        </div>
        <div className="flex-1 space-y-2">
          <Input value={sec.titulo || ""} onChange={(e) => onPatch({ titulo: e.target.value })}
            placeholder="Nombre de la sección (ej: Compareciente 1)"
            data-testid={`seccion-titulo-${sIdx}`}
            className="h-9 bg-white border-[#E5DFD3] font-display text-base focus-visible:ring-[#7A1E2E]" />
          <Input value={sec.descripcion || ""} onChange={(e) => onPatch({ descripcion: e.target.value })}
            placeholder="Descripción opcional de la sección"
            className="h-9 bg-white border-[#E5DFD3] text-xs focus-visible:ring-[#7A1E2E]" />
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button onClick={() => onMove(-1)} disabled={!canMoveUp} title="Subir sección"
            className="h-7 w-7 rounded-md hover:bg-white disabled:opacity-30 text-[#4a3838] flex items-center justify-center">
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
          <button onClick={() => onMove(1)} disabled={!canMoveDown} title="Bajar sección"
            className="h-7 w-7 rounded-md hover:bg-white disabled:opacity-30 text-[#4a3838] flex items-center justify-center">
            <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
          <button onClick={onDelete} disabled={!canDelete} title="Eliminar sección"
            data-testid={`seccion-delete-${sIdx}`}
            className="h-7 w-7 rounded-md hover:bg-[#FBEDEF] disabled:opacity-30 text-[#7A1E2E] flex items-center justify-center">
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {(sec.campos || []).map((c, cIdx) => (
          <CampoEditor
            key={cIdx} idx={cIdx} campo={c}
            onChange={(patch) => onUpdCampo(cIdx, patch)}
            onRemove={() => onRemoveCampo(cIdx)}
            onMove={(dir) => onMoveCampo(cIdx, dir)}
            canMoveUp={cIdx > 0} canMoveDown={cIdx < sec.campos.length - 1}
          />
        ))}
        {(!sec.campos || sec.campos.length === 0) && (
          <div className="text-center py-6 rounded-lg border-2 border-dashed border-[#E5DFD3] text-xs text-[#6B5D57]">
            Sin campos en esta sección
          </div>
        )}
        <Button onClick={onAddCampo} data-testid={`seccion-add-campo-${sIdx}`} size="sm" variant="outline"
          className="w-full border-dashed border-[#E5DFD3] bg-transparent hover:bg-[#F5EEE0] text-[#7A1E2E]">
          <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Agregar campo a esta sección
        </Button>
      </div>
    </div>
  );
}

function CampoEditor({ idx, campo, onChange, onRemove, onMove, canMoveUp, canMoveDown }) {
  const needsOptions = ["select", "multiselect"].includes(campo.tipo);
  const [optsText, setOptsText] = useState((campo.options || []).join("\n"));
  useEffect(() => { setOptsText((campo.options || []).join("\n")); /* eslint-disable-line */ }, [campo.tipo]);

  const commitOpts = (text) => {
    setOptsText(text);
    onChange({ options: text.split("\n").map((s) => s.trim()).filter(Boolean) });
  };

  return (
    <div className="rounded-lg bg-white border border-[#E5DFD3] p-4" data-testid={`campo-editor-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="pt-2 text-[#B8AEA5]"><GripVertical className="h-4 w-4" strokeWidth={1.8} /></div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className={labelCls}>Nombre del campo *</Label>
            <Input value={campo.label} onChange={(e) => onChange({ label: e.target.value })}
              placeholder="Ej: Nombre completo" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <Label className={labelCls}>Tipo</Label>
            <Select value={campo.tipo} onValueChange={(v) => onChange({ tipo: v })}>
              <SelectTrigger className={`${inputCls} mt-1.5`}><SelectValue /></SelectTrigger>
              <SelectContent>{TIPOS_CAMPO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className={labelCls}>Placeholder / ayuda breve</Label>
            <Input value={campo.placeholder || ""} onChange={(e) => onChange({ placeholder: e.target.value })}
              placeholder="Texto que se muestra en el campo vacío" className={`${inputCls} mt-1.5`} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-[#1A0810]">
              <Switch checked={!!campo.required} onCheckedChange={(v) => onChange({ required: v })} />
              Requerido
            </label>
          </div>
          {needsOptions && (
            <div className="md:col-span-3">
              <Label className={labelCls}>Opciones (una por línea) *</Label>
              <Textarea value={optsText} onChange={(e) => commitOpts(e.target.value)}
                placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
                className="mt-1.5 min-h-[80px] bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => onMove(-1)} disabled={!canMoveUp} title="Subir"
            className="h-7 w-7 rounded-md hover:bg-[#F5EEE0] disabled:opacity-30 text-[#4a3838] flex items-center justify-center">
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
          <button onClick={() => onMove(1)} disabled={!canMoveDown} title="Bajar"
            className="h-7 w-7 rounded-md hover:bg-[#F5EEE0] disabled:opacity-30 text-[#4a3838] flex items-center justify-center">
            <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
          <button onClick={onRemove} title="Eliminar campo"
            className="h-7 w-7 rounded-md hover:bg-[#FBEDEF] text-[#7A1E2E] flex items-center justify-center">
            <X className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Auto-mapea campos de un cliente al conjunto de campos de una sección por nombre.
// Retorna un objeto { campo_id: valor } listo para inyectar en `respuestas`.
const _norm = (s) => (s || "").toString().toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const buildPrefillFromCliente = (cliente, campos) => {
  if (!cliente || !Array.isArray(campos)) return {};
  const dir = cliente.direccion || {};
  const direccionCompleta = [dir.provincia, dir.canton, dir.distrito, dir.detalle]
    .filter(Boolean).join(", ");
  const prefill = {};
  for (const c of campos) {
    const l = _norm(c.label);
    let v = null;
    if (c.tipo === "cedula" || l.includes("cedula") || l.includes("identifica") || l.includes("dimex") || l.includes("pasaporte")) {
      v = cliente.identificacion || null;
    } else if (l.includes("whatsapp")) {
      v = cliente.whatsapp || cliente.telefono || null;
    } else if (c.tipo === "tel" || l.includes("telefono") || l.includes("celular") || l === "tel") {
      v = cliente.telefono || cliente.whatsapp || null;
    } else if (c.tipo === "email" || l.includes("email") || l.includes("correo")) {
      v = cliente.email || null;
    } else if (l.includes("nombre") || l.includes("compareciente") || l.includes("razon social") || l.includes("cliente")) {
      v = cliente.nombre || null;
    } else if (l.includes("direccion") || l.includes("domicilio") || l.includes("residencia")) {
      v = direccionCompleta || null;
    } else if (l.includes("ocupacion") || l.includes("profesion") || l.includes("actividad")) {
      v = cliente.ocupacion || null;
    }
    if (v !== null && v !== undefined && v !== "") {
      prefill[c.id] = v;
    }
  }
  return prefill;
};

function EnviarDialog({ formulario, clientes, onClose, onSent }) {
  const [clienteId, setClienteId] = useState("");
  const [dias, setDias] = useState(30);
  const [envio, setEnvio] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pre-cargas por sección: { [seccionId]: clienteId }
  const secciones = useMemo(() => toSecciones(formulario), [formulario]);
  const [prefillBySec, setPrefillBySec] = useState({});

  // Construye el objeto `respuestas` combinando los mapeos de cada sección
  const respuestasPreCargadas = useMemo(() => {
    const out = {};
    for (const sec of secciones) {
      const cid = prefillBySec[sec.id];
      if (!cid) continue;
      const cli = clientes.find((c) => c.id === cid);
      if (!cli) continue;
      Object.assign(out, buildPrefillFromCliente(cli, sec.campos || []));
    }
    return out;
  }, [prefillBySec, secciones, clientes]);

  const totalPreCargados = Object.keys(respuestasPreCargadas).length;

  const setSectionClient = (secId, cliId) => {
    setPrefillBySec((s) => ({ ...s, [secId]: cliId === "none" ? "" : cliId }));
  };

  const generar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/formularios/${formulario.id}/envios`, {
        cliente_id: clienteId || null,
        expira_dias: Number(dias) || 30,
        respuestas: totalPreCargados > 0 ? respuestasPreCargadas : null,
      });
      setEnvio(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al generar");
    } finally { setBusy(false); }
  };

  const publicUrl = envio ? `${window.location.origin}/f/${envio.token}` : "";

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Enlace copiado");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#FBF9F4] border-[#E5DFD3]" data-testid="enviar-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2">
            <Send className="h-5 w-5 text-[#1F5B2E]" strokeWidth={2} /> Enviar formulario
          </DialogTitle>
          <DialogDescription>{formulario.titulo}</DialogDescription>
        </DialogHeader>

        {!envio ? (
          <div className="space-y-4">
            <div>
              <Label className={labelCls}>Vincular a cliente (opcional)</Label>
              <Select value={clienteId || "none"} onValueChange={(v) => setClienteId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="enviar-cliente" className={`${inputCls} mt-2`}>
                  <SelectValue placeholder="Sin vincular (genérico)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin vincular</SelectItem>
                  {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-[#6B5D57]">Este es el "dueño" del envío (para historial). Los comparecientes se cargan abajo.</p>
            </div>

            <div className="rounded-lg bg-[#F5EEE0] border border-[#E5DFD3] p-3.5 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#7A1E2E] font-semibold">Pre-cargar comparecientes</div>
                  <div className="text-[11px] text-[#6B5D57] mt-0.5">
                    Elegí un cliente guardado por sección: los campos <em>nombre, cédula, teléfono, email y dirección</em> se completan automáticamente.
                  </div>
                </div>
                {totalPreCargados > 0 && (
                  <Badge className="bg-[#1F5B2E] text-white border border-[#1F5B2E] font-semibold whitespace-nowrap">
                    {totalPreCargados} pre-cargados
                  </Badge>
                )}
              </div>
              <div className="space-y-2">
                {secciones.map((sec, sIdx) => (
                  <div key={sec.id || sIdx} className="rounded-md bg-white border border-[#E5DFD3] p-2.5">
                    <div className="text-[11px] text-[#4a3838] font-semibold mb-1.5">
                      <span className="text-[#7A1E2E]">Sección {sIdx + 1}:</span> {sec.titulo || "General"}
                    </div>
                    <Select
                      value={prefillBySec[sec.id] || "none"}
                      onValueChange={(v) => setSectionClient(sec.id, v)}
                    >
                      <SelectTrigger data-testid={`enviar-prefill-sec-${sIdx}`} className="h-9 bg-white border-[#E5DFD3] text-sm focus-visible:ring-[#7A1E2E]">
                        <SelectValue placeholder="Sin pre-carga (el cliente lo llenará)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin pre-carga</SelectItem>
                        {clientes.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.nombre}{c.identificacion ? ` · ${c.identificacion}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className={labelCls}>Validez del enlace (días)</Label>
              <Input data-testid="enviar-dias" type="number" min="1" max="365" value={dias}
                onChange={(e) => setDias(e.target.value)} className={`${inputCls} mt-2`} />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-[#EEF6EE] border border-[#1F5B2E]/25 p-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#1F5B2E] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                <div className="flex-1">
                  <div className="font-medium text-[#155724]">Enlace generado</div>
                  <div className="text-[12px] text-[#3d5c3d] mt-0.5">
                    Copiá el enlace y compartilo por WhatsApp o correo con el cliente.
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-md bg-white border border-[#E5DFD3] p-3">
              <div className="text-[11px] uppercase tracking-wide text-[#6B5D57] font-semibold">Enlace público</div>
              <div className="mt-2 flex items-center gap-2">
                <Input readOnly value={publicUrl} data-testid="enviar-url" className={`${inputCls} font-mono text-xs`} />
                <Button onClick={copy} data-testid="enviar-copiar" size="icon" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-white flex-shrink-0">
                  <Copy className="h-4 w-4" strokeWidth={2} />
                </Button>
              </div>
              <a href={publicUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#7A1E2E] hover:text-[#4A0E1C] font-medium">
                Vista previa <ExternalLink className="h-3 w-3" strokeWidth={2} />
              </a>
            </div>
          </div>
        )}

        <DialogFooter>
          {!envio ? (
            <>
              <Button variant="outline" onClick={onClose} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
              <Button onClick={generar} disabled={busy} data-testid="enviar-generar"
                className="bg-[#1F5B2E] hover:bg-[#144320] text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LinkIcon className="h-4 w-4 mr-2" strokeWidth={2} />Generar enlace</>}
              </Button>
            </>
          ) : (
            <Button onClick={onSent} data-testid="enviar-done" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Listo, ir a Envíos
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function EnviosTable({ envios, loading, onDetalle, onEliminar }) {
  const [filter, setFilter] = useState("todos");
  const filtered = useMemo(() => {
    if (filter === "todos") return envios;
    return envios.filter((e) => e.estado === filter);
  }, [envios, filter]);

  if (loading) return <div className="p-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E] mx-auto" /></div>;

  if (envios.length === 0) {
    return (
      <div className="rounded-xl bg-white border-2 border-dashed border-[#E5DFD3] p-16 text-center">
        <Send className="h-10 w-10 text-[#C19B6C] mx-auto" strokeWidth={1.3} />
        <p className="mt-4 text-sm text-[#6B5D57]">Aún no has enviado ningún formulario a un cliente.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {[
          { v: "todos", l: "Todos" }, { v: "pendiente", l: "Pendientes" },
          { v: "completado", l: "Completados" }, { v: "expirado", l: "Expirados" },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`envios-filter-${f.v}`}
            className={`h-7 px-3 rounded-full text-[11px] font-semibold uppercase tracking-wide transition
              ${filter === f.v ? "bg-[#2B0913] text-[#F1EDE4]" : "bg-[#F5EEE0] text-[#4a3838] hover:bg-[#EEE4D2]"}`}>
            {f.l}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
              <TableHead className="text-[#3d2a2a] font-semibold">Formulario</TableHead>
              <TableHead className="text-[#3d2a2a] font-semibold">Cliente</TableHead>
              <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
              <TableHead className="text-[#3d2a2a] font-semibold">Enviado</TableHead>
              <TableHead className="text-[#3d2a2a] font-semibold">Respondido</TableHead>
              <TableHead className="text-[#3d2a2a] font-semibold">Enlace</TableHead>
              <TableHead className="text-right text-[#3d2a2a] font-semibold">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((env) => {
              const est = ESTADOS_ENVIO[env.estado] || ESTADOS_ENVIO.pendiente;
              const Icon = est.Icon;
              const publicUrl = `${window.location.origin}/f/${env.token}`;
              return (
                <TableRow key={env.id} data-testid={`envio-row-${env.id}`} className="border-b border-[#E5DFD3] hover:bg-[#FBF9F4]">
                  <TableCell className="text-sm text-[#1A0810] font-medium">{env.formulario_titulo}</TableCell>
                  <TableCell className="text-sm text-[#3d2a2a]">{env.cliente_nombre || <span className="text-[#B8AEA5]">—</span>}</TableCell>
                  <TableCell>
                    <Badge className={`${est.cls} font-semibold border inline-flex items-center gap-1`}>
                      <Icon className="h-3 w-3" strokeWidth={2.2} /> {est.label}
                    </Badge>
                    {env.escritura_id && (
                      <div className="mt-1 text-[10px] text-[#1F5B2E] font-semibold inline-flex items-center gap-1">
                        <FileCheck2 className="h-3 w-3" strokeWidth={2} /> Escritura creada
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-[#6B5D57]">{env.sent_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-xs text-[#6B5D57]">{env.submitted_at?.slice(0, 10) || "—"}</TableCell>
                  <TableCell>
                    <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); }}
                      data-testid={`envio-copiar-${env.id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#7A1E2E] hover:text-[#4A0E1C] font-semibold">
                      <Copy className="h-3 w-3" strokeWidth={2} /> Copiar
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => onDetalle(env)} data-testid={`envio-detalle-${env.id}`}
                        className="h-8 w-8 hover:bg-[#F5EEE0]" title="Ver respuestas">
                        <Eye className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onEliminar(env)} data-testid={`envio-eliminar-${env.id}`}
                        className="h-8 w-8 hover:bg-[#FBEDEF] text-[#7A1E2E]" title="Eliminar">
                        <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ============================================================================
function EnvioDetalleDialog({ envio: env, clientes, onClose, onChange }) {
  const navigate = useNavigate();
  const [full, setFull] = useState(null);
  const [linkCliente, setLinkCliente] = useState("");
  const [numero, setNumero] = useState("");
  const [tipoEsc, setTipoEsc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/formularios/envios/${env.id}`).then((r) => setFull(r.data)).catch(() => {});
  }, [env.id]);

  const vincular = async () => {
    if (!linkCliente) return;
    try {
      await api.post(`/formularios/envios/${env.id}/cliente`, { cliente_id: linkCliente });
      toast.success("Cliente vinculado");
      setLinkCliente("");
      const r = await api.get(`/formularios/envios/${env.id}`);
      setFull(r.data);
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo vincular");
    }
  };

  const crearEscritura = async () => {
    const n = parseInt(numero, 10);
    if (!n || n < 1) { toast.error("Ingresá el número de escritura"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/formularios/envios/${env.id}/crear-escritura`, { numero: n, tipo: tipoEsc || "" });
      toast.success(`Escritura #${data.numero} creada`);
      onChange?.();
      navigate("/notarial");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo crear");
    } finally { setBusy(false); }
  };

  const secciones = full?.formulario ? toSecciones(full.formulario) : [];
  const respuestas = full?.respuestas || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="envio-detalle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810] flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8} />
            {env.formulario_titulo}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <Badge className={`${(ESTADOS_ENVIO[env.estado] || ESTADOS_ENVIO.pendiente).cls} font-semibold border`}>
              {(ESTADOS_ENVIO[env.estado] || ESTADOS_ENVIO.pendiente).label}
            </Badge>
            {env.cliente_nombre && (
              <span className="text-xs text-[#6B5D57] inline-flex items-center gap-1">
                <User className="h-3 w-3" strokeWidth={2} /> {env.cliente_nombre}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!full ? (
          <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#7A1E2E] mx-auto" /></div>
        ) : env.estado !== "completado" ? (
          <div className="rounded-lg bg-[#FBEED4] border border-[#E5C892] p-4 text-sm text-[#7A5312]">
            {env.estado === "expirado" ? "Este envío ha expirado y ya no puede recibir respuestas." : "El cliente aún no ha completado el formulario."}
            <div className="mt-2 text-xs">Enlace: <code className="font-mono">{window.location.origin}/f/{env.token}</code></div>
          </div>
        ) : (
          <div className="space-y-4">
            {secciones.map((sec, sIdx) => (
              <div key={sec.id || sIdx} className="rounded-lg bg-white border border-[#E5DFD3] overflow-hidden">
                {(sec.titulo || secciones.length > 1) && (
                  <div className="bg-[#F5EEE0] px-4 py-2 border-b border-[#E5DFD3]">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A1E2E] font-semibold">Sección {sIdx + 1}</div>
                    <div className="font-display text-base text-[#1A0810]">{sec.titulo || "General"}</div>
                    {sec.descripcion && <div className="text-xs text-[#6B5D57] mt-0.5">{sec.descripcion}</div>}
                  </div>
                )}
                <div className="divide-y divide-[#E5DFD3]">
                  {(sec.campos || []).map((c) => {
                    const v = respuestas[c.id];
                    const display = v === null || v === undefined || v === ""
                      ? <span className="text-[#B8AEA5]">—</span>
                      : Array.isArray(v) ? v.join(", ")
                      : typeof v === "boolean" ? (v ? "Sí" : "No")
                      : String(v);
                    return (
                      <div key={c.id} className="p-3">
                        <div className={labelCls}>{c.label}</div>
                        <div className="mt-1 text-sm text-[#1A0810] whitespace-pre-wrap">{display}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {!env.cliente_id && (
              <div className="rounded-lg bg-[#FFFBEF] border border-[#E7C875] p-4">
                <div className="font-semibold text-sm text-[#1A0810] mb-2">Vincular a un cliente registrado</div>
                <div className="flex items-center gap-2">
                  <Select value={linkCliente} onValueChange={setLinkCliente}>
                    <SelectTrigger data-testid="link-cliente-select" className={inputCls}><SelectValue placeholder="Seleccioná un cliente" /></SelectTrigger>
                    <SelectContent>
                      {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={vincular} disabled={!linkCliente} data-testid="link-cliente-btn"
                    className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
                    Vincular
                  </Button>
                </div>
              </div>
            )}

            {!env.escritura_id ? (
              <div className="rounded-lg bg-[#EEF6EE] border border-[#1F5B2E]/25 p-4">
                <div className="font-semibold text-sm text-[#155724] mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" strokeWidth={2} /> Crear escritura desde este envío
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className={labelCls}>Número de escritura *</Label>
                    <Input data-testid="crear-esc-numero" type="number" min="1" value={numero}
                      onChange={(e) => setNumero(e.target.value)} placeholder="123" className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <Label className={labelCls}>Tipo (opcional)</Label>
                    <Select value={tipoEsc || "auto"} onValueChange={(v) => setTipoEsc(v === "auto" ? "" : v)}>
                      <SelectTrigger data-testid="crear-esc-tipo" className={`${inputCls} mt-2`}>
                        <SelectValue placeholder={full?.formulario?.tipo_escritura || "Auto desde plantilla"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (desde plantilla)</SelectItem>
                        {TIPOS_ESCRITURA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={crearEscritura} disabled={busy} data-testid="crear-esc-btn"
                  className="mt-4 bg-[#1F5B2E] hover:bg-[#144320] text-white">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Gavel className="h-4 w-4 mr-2" strokeWidth={2} />Crear escritura y abrir Notarial</>}
                </Button>
              </div>
            ) : (
              <div className="rounded-lg bg-[#EEF6EE] border border-[#1F5B2E]/25 p-3 text-sm text-[#155724] flex items-center gap-2">
                <FileCheck2 className="h-4 w-4" strokeWidth={2} /> Ya se creó una escritura desde este envío.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
